import {defineStore} from 'pinia'
import {useToast} from "vue-toastification";
import axios from 'axios'
import {updateDatadogContext} from "@/stores/includes/datadog";
import { useAppStore } from "@/stores/app";
import {useAffiliate} from "@/stores/affiliate";

const toast = useToast()

export const useUserStore = defineStore('user', {
    // Initial state of store
    state: () => ({ 
        user: null,
        lastVerificationMailSentAt: null,
        teamExpirePopupConfirmed: false,
        paymentPastDueConfirmed: false,
        features: null,
        permissions: null,
        address: null,
        paymentMethod: null,
    }),
    getters: {
        isTeamMember: state => !!state.user?.team_id,
        isSelfPayer(state) {
            return !this.isTeamMember || (this.isTeamMember && state.user?.selfpayer);
        },
        isAdmin: state => !!state.user?.roles?.some(role => role.name === 'Administrator'),
    },
    // Available actions
    actions: {
        reset() {
            // Remove from localStorage
            localStorage.removeItem('userData')
        },
        buildFormData(data) {
            const formData = new FormData()
            Object.keys(data).forEach(key => {
                // Only add if not empty
                if (data[key]) formData.append(key, data[key])
                else formData.append(key, '')
            })
            return formData
        },
        async getUserByEmail(email){
            try {
                 return await axios.get(`/api/user/${email}`)
            }
            catch (e) {
            }
        },
        async resetReactivatedFlag() {
            await axios.post('/api/user/reset-reactivated-flag',{
                id: this.user.id
            })
        },
        async get() {
            try {
                const response = await axios.get(`/api/user`)

                this.user = response.data.data
                this.features = response.data.features
                this.permissions = response.data.permissions
                this.address = response.data.data.address

                // Set also at local storage
                // This should not be necessary anymore, since it was only done to determine if the user is logged in
                // and we changed that, however to reduce the risk of breaking stuff, we leave that here and remove that
                // later
                localStorage.setItem("userData", JSON.stringify(this.user))

                // Update dd context
                updateDatadogContext()

                console.info("Got user")

            } catch (error) {
                toast.error("Fehler beim laden der Daten für den aktuellen User")
                console.error(error);
            }
        },
        async register(content) {
            try {
                const response = await axios.post(`/api/user/register`, content)
                return true
            } catch (error) {
                // The caller branches on the contract's stable code (TSD-320). The previous
                // `data?.error !== null` guard threw on any response without a JSON body.
                const code = error.response?.data?.code

                if (code) return code

                console.error(error)
                toast.error("Leider ist die Registrierung fehlgeschlagen. Wende dich bitte an den Support!")
            }
            return false
        },
        async resendVerificationMail() {
            try {
                const response = await axios.post(`/api/user/email/verification/resend`, {
                    email : this.user.email
                })
                this.lastVerificationMailSentAt = response.data.sent_at
                toast.success("E-Mail erneut gesendet")
                return true
            } catch (error) {
                // If we have a 400 error, it is, because the user is already verified
                console.log(error)
                if (error.response.status === 400) {
                    return null
                }
                toast.error("Leider ist ein Fehler bei senden der Mail aufgetreten")
                return false
            }
        },
        async updateInitialLastLogin() {
            try {
                await axios.post('api/user/login/initial');
            }
            catch(error) {
                toast.error('Fehler beim initialen einloggen des Users');
            }
        },
        async updateProfilePicture(image) {
            // 2_097_152 bytes == 2 MiB
            if (image.size > 2_097_152) {
                toast.error("Das Bild darf nicht größer als 2MB sein um die Ladezeiten gering zu halten!")
                return
            }

            // Build request
            let formData = new FormData();
            formData.append("image", image);

            try {
                const response = await axios.post(`/api/user/avatar`, formData)
                this.user.avatar = response.data.data.avatar
                this.user.avatar_path = response.data.data.avatar_path
                toast.success("Profilbild aktualisiert")
            } catch (error) {
                toast.error("Fehler beim hochladen des Profilbilds, probiere es am besten nochmal oder verwende ein anderes Bild")
            }
        },
        async deleteProfilePicture() {
            try {
                const response = await axios.post(`/api/user/avatar`, this.buildFormData({
                    image: null
                }))
                this.user.avatar = response.data.data.avatar
                this.user.avatar_path = response.data.data.avatar_path
                toast.success("Profilbild gelöscht")
                return true
            } catch (error) {
                toast.error("Fehler beim Löschen des Profilbilds")
            }
            return false
        },
        async updateProfile(content) {
            try {
                const response = await axios.put(`/api/user/${this.user.id}`, content)
                toast.success("Profil aktualisiert")
                this.user = response.data
                return true
            } catch (error) {
                toast.error("Leider ist das aktualisieren des Profils fehlgeschlagen")
                console.error(error)
            }
            return false
        },
        async setAddress(content){
            try {
                const response = await axios.post(`/api/user/address/set`, content)
                this.user = response.data
                toast.success("Adresse gespeichert")
                return true
            } catch (error) {
                if(error.response?.status === 422 && error.response?.data?.errors) {
                    toast.error("Bitte prüfe deine Eingaben")
                    return { success: false, errors: error.response.data.errors }
                }
                toast.error("Leider ist das speichern der Adresse fehlgeschlagen")
            }
            return false
        },
        async setInvoiceEmail(invoiceEmail) {
            try {
                const response = await axios.post(`/api/user/invoice-email`, {
                    invoice_email: invoiceEmail || null
                })
                this.user = response.data
                toast.success("Rechnungs-E-Mail gespeichert")
                return true
            } catch (error) {
                if(error.response?.status === 422 && error.response?.data?.errors) {
                    toast.error("Bitte gib eine gültige E-Mail-Adresse ein")
                    return { success: false, errors: error.response.data.errors }
                }
                toast.error("Fehler beim Speichern der Rechnungs-E-Mail")
                console.error(error)
            }
            return false
        },
        async setFavicon(favicon) {
            try {
                const response = await axios.post(`/api/user/settings/favicon`, this.buildFormData({
                    favicon: favicon
                }))
                this.user.favicon = response.data.data.favicon
                this.user.favicon_path = response.data.data.favicon_path
                return true
            } catch (error) {
                toast.error("Fehler beim speichern des Favicons")
            }
            return false
        },
        async setCompanyLogo(logo, dark = false) {
            try {
                const formData = this.buildFormData({ logo: logo })
                if(dark) formData.append('dark', '1')

                const response = await axios.post(`/api/user/settings/logo`, formData)

                if(dark) {
                    this.user.company_logo_dark = response.data.data.company_logo_dark
                    this.user.company_logo_dark_path = response.data.data.company_logo_dark_path
                } else {
                    this.user.company_logo = response.data.data.company_logo
                    this.user.company_logo_path = response.data.data.company_logo_path
                }

                return true
            } catch (error) {
                toast.error("Fehler beim speichern des Firmenlogos")
            }
            return false
        },
        async setBrandingColor(brandingColor) {
            try {
                const response = await axios.post(`/api/user/settings/color`, {
                    branding_color: brandingColor || null
                })

                this.user.branding_color = response.data.data.branding_color

                return true
            } catch (error) {
                toast.error("Fehler beim Speichern der Firmenfarbe")
                console.error(error)
            }
            return false
        },
        async setShowTellScaleBranding(value) {
            const previous = this.user.show_tellscale_branding ?? true;

            this.user.show_tellscale_branding = value;

            try {
                await axios.post(`/api/user/settings/tellscale-branding`, {
                    showTellScaleBranding: value
                });

                return true;
            } catch (error) {
                this.user.show_tellscale_branding = previous;
                toast.error("Fehler beim Speichern der TellScale Branding Einstellung");
                console.error(error);
            }
            return false;
        },
        async redirectOnIncompleteRegistration() {
            // If no user is loaded we can skip that too
            if (this.user === null) return false

            // If registered correctly we can just skip that
            if (this.user.registration_success && this.user.last_login_at) return false

            // User not registered correctly, redirect to registration page to finish it up
            let route = 'signup-success'

            // Determine correct route
            switch (this.user.registration_status) {
                case 'REGISTER_ADDRESS': route = 'signup-two'; break;
                case 'REGISTER_PAYMENT': route = 'payment-selection'; break;
            }

            console.info("Redirecting to " + route)

            // Redirect
            await this.router.replace('/' + route)

            return true;
        },
        async getPaymentMethod() {
            try {
                const response = await axios.get(`/api/user/payment/method`)
                this.paymentMethod = response.data
            } catch (error) {
                toast.error("Fehler beim laden der Zahlungsdaten")
                console.error(error);
            }
        },
        async getPricingInfo() {
            try {
                const { data } = await axios.get('/api/user/pricing-info');

                return data;
            }
            catch(error) {
                toast.error("Fehler beim laden der Kostenübersicht")
                console.log(error);
                return null;
            }
        },
        hasFeature(feature) {
            if(feature === null || feature === undefined) return true;

            if(this.features == null) return false;

            const app = useAppStore();

            return (app.isFeatureEnabled(feature) && this.features.includes(feature)) || this.user.can_use_all_features;
        },
        hasPermissionTo(permission) {
            if(this.permissions === null || this.permission === undefined) return false;

            return !!this.permissions.find(_permission => _permission === permission);
        },
        hasRole(role) {
            if(role === null || role === undefined) return false;

            return !!this.user?.roles?.find(_role => _role.name === role);
        },
        // async updateSettings(payload) {
        //     try {
        //         const response = await axios.post(`/api/user/data-share/update`, payload);
        //         this.user.share_profile_picture = response.data.data.share_profile_picture;
        //         this.user.share_name = response.data.data.share_name;
        //         this.user.cookie_banner_active = response.data.data.cookie_banner_active;
        //         toast.success("Einstellungen gespeichert");
        //         return true;
        //     } catch(error) {
        //         toast.error("An error occurred while updating settings");
        //         console.error(error);
        //         return false;
        //     }
        // },
        async updateShareProfilePicture(value) {
            try {
                const response = await axios.post(`/api/user/data-share/update/profile-picture`, {
                    shareProfilePicture: value
                });
                this.user.share_profile_picture = response.data.data.shareProfilePicture;
                toast.success("Profilbild-Einstellung gespeichert");
                return true;
            } catch (error) {
                toast.error("Fehler beim Speichern der Profilbild-Einstellung");
                console.error(error);
                return false;
            }
        },

        async updateShareName(value) {
            try {
                const response = await axios.post(`/api/user/data-share/update/name`, {
                    shareName: value
                });
                this.user.share_name = response.data.data.shareName;
                toast.success("Namensfreigabe gespeichert");
                return true;
            } catch (error) {
                toast.error("Fehler beim Speichern der Namensfreigabe");
                console.error(error);
                return false;
            }
        },

        async updateCookieBannerActive(value) {
            try {
                const response = await axios.post(`/api/user/data-share/update/cookie-banner`, {
                    cookieBannerActive: value
                });
                this.user.cookie_banner_active = response.data.data.cookieBannerActive;
                toast.success("Cookie-Banner-Einstellung gespeichert");
                return true;
            } catch (error) {
                toast.error("Fehler beim Speichern der Cookie-Banner-Einstellung");
                console.error(error);
                return false;
            }
        },

        async updateImposterLoginEnabled(value) {
            try {
                const response = await axios.post(`/api/user/data-share/update/imposter-login`, {
                    imposterLoginEnabled: value
                });
                this.user.imposter_login_enabled = response.data.data.imposterLoginEnabled;
                toast.success("Imposter-Login-Einstellung gespeichert");
                return true;
            } catch (error) {
                toast.error("Fehler beim Speichern der Imposter-Login-Einstellung");
                console.error(error);
                return false;
            }
        },
    },
  })